/**
 * Meter theory (topics T1, T2 and T7, design §12), pure.
 *
 * Conventions:
 * - Durations are exact fractions of a whole note (`Frac`): a quarter is 1/4,
 *   a dotted quarter 3/8, a triplet eighth 1/12. Floats would drift under
 *   tuplets and bar-fill checks need exact equality.
 * - A **pulse** is the note the bottom number names, the unit the tempo is
 *   counted in, as in the timing engine (`timing/meter.ts`): quarters in 4/4,
 *   eighths in 6/8 and 7/8.
 * - `groups` split a bar's pulses: `[1, 1, 1, 1]` for 4/4, `[3, 3]` for 6/8,
 *   `[2, 2, 3]` for 7/8 counted 2+2+3, `[3, 2]` for 5/4 counted 3+2.
 * - **Felt beats** are what a listener taps: one per pulse in simple time
 *   (4/4 = 4, 2/2 = 2), one per dotted group in compound time (6/8 = 2,
 *   12/8 = 4), and in odd meters one per group when the pulse is an eighth or
 *   shorter (7/8 = 3 uneven beats) but one per pulse when it is a quarter or
 *   longer (5/4 = 5 beats, accented 3+2).
 */

// ---------------------------------------------------------------------------
// Exact fractions

/** A fraction in lowest terms with a positive denominator. */
export interface Frac {
  readonly n: number;
  readonly d: number;
}

                                            
                      
                      
                                
           
 

                                              
                                                              
                                                        
                           
                              
                                                       
 

                                  

                                                 
                                                
 

                                                 
                                                
 

                                                 
                                    
 

                                                 
                                                          
                                    
 

/** Negative, zero or positive as `a` is less than, equal to or more than `b`. */
                                                   
                               
 

                                                   
                                    
 

                                                       
                                     
 

                                               
                   
 

/** "3/8", "1", "-1/4". */
                                               
                                                   
 

/** True when the denominator is a power of two: a length the plain note values can spell. */
                                            
                                 
 

// ---------------------------------------------------------------------------
// Note values

/** A note value by its denominator: 1 = whole, 4 = quarter, 16 = sixteenth. */
export type NoteValue = 1 | 2 | 4 | 8 | 16 | 32 | 64;

                                                                          

                                                        
                                                        
 

/** A note value with its dots, the unit a beat is felt in. */
export interface Duration {
  readonly value: NoteValue;
  readonly dots: 0 | 1 | 2;
}

/** The length of a value with dots: a dot adds half, a second dot a quarter. */
                                                                         
                                                                 
                                                      
 

                                                   
                                     
 

                                                
             
            
               
              
                  
                      
                     
  

/** "dotted quarter", "eighth". */
                                                   
                                                                               
 

// ---------------------------------------------------------------------------
// Time signatures

/** The bottom numbers a time signature may have. */
export type MeterUnit = 2 | 4 | 8 | 16;

export type MeterKind = 'simple' | 'compound' | 'odd';

export interface TimeSignature {
  /** Pulses per bar (the top number). */
  readonly top: number;
  /** The pulse's note value (the bottom number). */
  readonly bottom: MeterUnit;
  /** Pulses per group, in order; they sum to `top`. */
  readonly groups: readonly number[];
  /** Written as a symbol: common time (C, 4/4) or cut time (¢, 2/2). */
  readonly symbol?: 'common' | 'cut';
}

                                                 
                                                   
 

                                              
                                  
 

/** The usual grouping of a bar of `top` pulses. */
                                                      
                                                  
                                                                
                               
                                  
                                                                    
                                     
                                                                                          
 

/**
 * A time signature. `groups` is needed only for a grouping other than the
 * usual one (`7/8` is 2+2+3 unless told 3+2+2). Equal groups must be ones
 * (simple, at most four beats) or threes (compound): 4/8 grouped 2+2 is 2/4,
 * and 6/8 grouped in ones is 3/4 or 6/4.
 */
                              
              
                 
                             
                            
                  
                                                    
                                                               
                                                                                             
                                                      
                                                                     
                                                                   
                                           
                                                                         
                                          
                                                                    
                                                                             
                                                                                            
                                                                                         
                                                                                  
 

                                                                    
                                                              

const TS_TEXT = /^(?:(\d+)|(\d+(?:\+\d+)+))\/(\d+)(?:\s*\((\d+(?:\+\d+)+)\))?$/;

/**
 * Reads "4/4", "6/8", "7/8 (3+2+2)", "3+2+2/8", "C" (common time) and "¢"
 * (cut time; "cut" and "common" also work).
 */
                                                                 
                        
                                                      
                                                
                            
                                                                  
                                                  
                                                                                  
                                                                
                                                                         
                                                    
 

                                                         
                             
                                                                                      
               
 

/**
 * "6/8", "2/2", and for odd meters the grouping too: "7/8 (2+2+3)", as the
 * timing engine's `METER_PRESETS` are keyed.
 */
                                                              
                                        
                                                                             
 

                                                       
                                         
 

/** A bar's length: 3/4 is three quarters, 6/8 three quarters too. */
                                                    
                                 
 

                                                      
                            
 

/** True when the felt beats are the groups, false when they are the pulses. */
                                                     
                             
                                                                   
 

/** One felt beat: where it starts, how long it is, and how many pulses it holds. */
export interface Beat {
  readonly start: Frac;
  readonly length: Frac;
  readonly pulses: number;
}

/** The felt beats of a bar, in order. */
                                                  
                                                                               
                         
                   
                               
                                           
                                        
                                   
   
             
 

/** How many beats a listener taps per bar: 6/8 → 2, 2/2 → 2, 7/8 → 3, 5/4 → 5. */
                                                      
                                                        
 

/**
 * The note value one felt beat lasts: a quarter in 4/4, a dotted quarter in
 * 6/8, a half in cut time. `null` when the beats are uneven (7/8 = 2+2+3).
 */
                                                              
                                                                
                                                                                            
              
 

/** "duple", "triple", "quadruple", or the count for larger meters. */
                                           
                                                                                                   
 

/**
 * A one-line description for explanations and feedback:
 * "Compound duple: 2 dotted-quarter beats, each split in 3",
 * "Odd meter: 3 uneven beats, grouped 2+2+3".
 */
                                                          
                             
                          
                            
                       
                                                                       
                                                                     
   
                                          
                                                     
                                                                                                                        
 

// ---------------------------------------------------------------------------
// Beaming and the click

/**
 * The spans eighths and shorter beam within, covering the bar in order. Beams
 * show the beat: one span per beat (per dotted beat in compound time, per
 * group in odd eighth meters). Where the beat is itself an eighth, eighths
 * beam in pairs, and 3/8 beams as one bar.
 */
                                                      
                             
                                                                                 
                                           
                           
                                                                                              
               
 

/** The beam spans as VexFlow `Beam.generateBeams` groups: 7/8 (2+2+3) → ["2/8", "2/8", "3/8"]. */
                                                                
                                                                             
 

/**
 * The timing engine's click meter (`ClickMeter` in `timing/meter.ts`, the
 * same shape): a simple meter is one group, so only its downbeat is accented;
 * compound and odd meters accent each group.
 */
                                                                                      
          
                                                                   
                    
    
 

export { TS_TEXT };
